import { isFloatingRateLoanProductFields } from '@src/types/OTC/otcGuards';
import type { DealProductBuilderRow } from '@src/types/OTC/otcProductBuilderTypes';
import {
  BoolEnum,
  logger,
  runValidationV1WithResult,
  SubProductTypeEnum,
  type LoanQuoteTerminationTypeEnum,
  type OTCFloatingRateLoanProductTemplate,
  type PrepaymentOptionEnum,
} from '@talos/kyoko';
import { isEqual } from 'lodash-es';
import { BaseProductDefinition, type CreateProductParams } from './BaseProductDefinition';
import type { CreateFloatingRateLoanRequest, FloatingRateLoanProductDetails } from './CustomInstrumentTypes';
import {
  floatingRateLoanProductBuilderSchema,
  type ValidatedFloatingRateLoanRowData,
} from './FloatingRateLoanProductBuilderSliceStrategy';

export class FloatingRateLoanProductDefinition extends BaseProductDefinition {
  private otcFloatingRateLoansByDisplaySymbol: Map<string, OTCFloatingRateLoanProductTemplate> = new Map();

  updateReferenceData(otcFloatingRateLoansByDisplaySymbol: Map<string, OTCFloatingRateLoanProductTemplate>) {
    this.otcFloatingRateLoansByDisplaySymbol = otcFloatingRateLoansByDisplaySymbol;
  }

  buildPostBody(row: ValidatedFloatingRateLoanRowData): CreateFloatingRateLoanRequest | undefined {
    const validationRun = runValidationV1WithResult(floatingRateLoanProductBuilderSchema, row);
    if (validationRun.result === 'failure') {
      return;
    }

    const validatedData = validationRun.validatedData;

    // termType is already validated as LoanQuoteTerminationTypeEnum, so we can use it directly
    const termType = validatedData.otcFields.termType as LoanQuoteTerminationTypeEnum;

    const productDetails: FloatingRateLoanProductDetails = {
      PaymentPeriod: validatedData.otcFields.paymentPeriod,
      UnitLoanAmount: validatedData.otcFields.unitLoanAmount,
      BorrowedAsset: validatedData.baseAsset,
      ResetPeriod: validatedData.otcFields.resetPeriod,
      DayCountConvention: validatedData.otcFields.dayCountConvention,
      LoanPaymentCcy: validatedData.otcFields.loanPaymentCcy,
      TermType: termType,
      PrepaymentOption: validatedData.otcFields.prepaymentOption as PrepaymentOptionEnum,
      RateSource: validatedData.otcFields.rateSource,
      AccrualPeriod: validatedData.otcFields.accrualPeriod,
      CallOption: (validatedData.otcFields.callOption ? BoolEnum.True : BoolEnum.False) as BoolEnum,
      ...(validatedData.otcFields.term && { Term: validatedData.otcFields.term }),
      ...(validatedData.collateralAsset && { CollateralAsset: validatedData.collateralAsset }),
      ...(validatedData.otcFields.initialTerm && { InitialFixedTerm: validatedData.otcFields.initialTerm }),
      ...(validatedData.uniqueSuffix && { UniqueSuffix: validatedData.uniqueSuffix }),
    };

    const result: CreateFloatingRateLoanRequest = {
      ProductType: 'OTC',
      SubProductType: SubProductTypeEnum.OTCFloatingRateLoan,
      RequestType: 'OTC',
      ProductDetails: productDetails,
    };
    return result;
  }

  getProductDetailsForComparison(row: DealProductBuilderRow): CreateFloatingRateLoanRequest | null {
    if (!row.otcFields || !row.baseAsset || !isFloatingRateLoanProductFields(row.otcFields)) {
      return null;
    }

    const validation = runValidationV1WithResult(floatingRateLoanProductBuilderSchema, row);
    if (validation.result === 'failure') {
      throw new Error('Product builder validation failed');
    }

    return this.buildPostBody(validation.validatedData) || null;
  }

  checkForUniqueness(
    rowData: DealProductBuilderRow & { product: 'floatingRateLoan' }
  ): 'notReady' | 'uncreated' | 'conflict' | 'alreadyCreated' {
    const validation = runValidationV1WithResult(floatingRateLoanProductBuilderSchema, rowData);
    if (validation.result === 'failure') {
      return 'notReady';
    }

    const productDetails = this.getProductDetailsForComparison(rowData);
    if (!productDetails) {
      return 'notReady';
    }

    const postBody = this.buildPostBody(validation.validatedData);
    if (!postBody) {
      return 'notReady';
    }
    const symbol =
      validation.validatedData.productTemplate +
      (postBody.ProductDetails.UniqueSuffix ? `-${postBody.ProductDetails.UniqueSuffix}` : '');

    const existingSecurity = this.otcFloatingRateLoansByDisplaySymbol.get(symbol);
    if (!existingSecurity) {
      return 'uncreated';
    }

    const existingProductDetails = existingSecurity?.ProductDetails;
    if (!isEqual(postBody.ProductDetails, existingProductDetails)) {
      return 'conflict';
    }

    return 'alreadyCreated';
  }

  async createProduct({
    rowData,
    createCustomInstrument,
    onSuccess,
    onError,
  }: CreateProductParams & {
    rowData: DealProductBuilderRow & { product: 'floatingRateLoan' };
  }): Promise<{ symbol: string } | undefined> {
    const validation = runValidationV1WithResult(floatingRateLoanProductBuilderSchema, rowData);
    if (validation.result === 'failure') {
      onError('Validation failed on OTC Floating Rate Loan');
      return;
    }

    try {
      this.checkForUniqueness(rowData);
      const validatedData = validation.validatedData;

      const loan = this.buildPostBody(validatedData);

      if (!loan) {
        onError('Failed to build floating rate loan product body');
        return;
      }

      const rows = await createCustomInstrument(loan);
      onSuccess('Floating rate loan product created successfully');
      logger.info('Floating rate loan product created successfully', { data: rows });

      // Return the symbol from the API response
      return { symbol: rows[0]?.Symbol ?? '' };
    } catch (error) {
      if (error instanceof Error && error.name === 'ValidationError') {
        onError(`Validation failed: ${error.message}`);
      } else {
        const serverMessage = this.getServerErrorMessage(error);
        onError(
          serverMessage
            ? `Error creating floating rate loan product: ${serverMessage}`
            : 'Error creating floating rate loan product'
        );
      }
      logger.error(new Error('Error creating floating rate loan product'), {
        extra: {
          input: { rowData },
          message: error instanceof Error ? error.message : 'Unknown error',
          error,
        },
      });
      return;
    }
  }
}
